// frontend/src/components/apply/AddressReviewDialog.tsx
// #1059 — the suggest-NEVER-block affordance. Opens only for a 'corrected' or
// 'unknown' verdict on the contact screen's advance; 'confirmed' and mode=off
// never render it. Every path out of this dialog either advances the family
// or returns them to editing — no path traps them: grant checks and cards ship
// to rural and new-construction addresses the postal database has never heard
// of, so "we couldn't verify it" is information, not a gate.
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { MonoLabel } from '@/components/editorial';
import type { AddressCandidate, AddressValidationData } from '@/types/applications';

interface AddressReviewDialogProps {
  open: boolean;
  /** The address exactly as the family typed it. */
  entered: AddressCandidate;
  /** The proxy's answer — only 'corrected' | 'unknown' verdicts reach here. */
  result: AddressReviewResult;
  /** Write the suggested values into the fields and continue (marker: confirmed). */
  onUseSuggested: () => void;
  /** Continue with the fields exactly as typed (marker: as_entered). */
  onKeepAsEntered: () => void;
  /** Close and stay on the step so the family can fix a typo themselves.
   *  Also fired when the dialog is dismissed — dismissal never advances. */
  onEditAddress: () => void;
  /** #2752: the /renew walk. "This never stops your renewal" once the
   *  reader's language has that line; until then the language's own
   *  application wording rather than English. */
  renewal?: boolean;
}

/** #2793: the codes the address drivers send beside each English message
 *  (backend/src/services/addressValidation/adapter.js lists them). */
const ADDRESS_MESSAGE_CODES = [
  'added_street_type',
  'matched_check_suggestion',
  'missing_component',
  'no_result',
  'not_verified',
  'standardized',
  'street_not_found',
  'street_required',
  'unconfirmed_component',
] as const;
type AddressMessageCode = (typeof ADDRESS_MESSAGE_CODES)[number];

/** Google component types the dialog can name in the family's language. */
const ADDRESS_COMPONENTS = [
  'administrative_area_level_1',
  'locality',
  'postal_code',
  'route',
  'street_number',
  'subpremise',
] as const;
type AddressComponent = (typeof ADDRESS_COMPONENTS)[number];

export interface AddressMessageCodeEntry {
  code: string;
  params?: Record<string, string>;
}

/** What the proxy answers, plus the #2793 codes array. */
export type AddressReviewResult = AddressValidationData & { codes?: AddressMessageCodeEntry[] };

const isKnownCode = (code: string): code is AddressMessageCode =>
  (ADDRESS_MESSAGE_CODES as readonly string[]).includes(code);
const isKnownComponent = (type: string): type is AddressComponent =>
  (ADDRESS_COMPONENTS as readonly string[]).includes(type);

function addressLines(a: AddressCandidate): string[] {
  const line1 = [a.street, a.apt].filter(Boolean).join(', ');
  const line2 = `${[a.city, a.state].filter(Boolean).join(', ')}${a.zip ? ` ${a.zip}` : ''}`.trim();
  return [line1, line2].filter(Boolean);
}

function AddressCard({ label, address, highlight }: { label: string; address: AddressCandidate; highlight?: boolean }) {
  return (
    <div className={`rounded-md border p-3 space-y-1 ${highlight ? 'border-stevens-indigo/40 bg-stevens-indigo/5' : 'border-border'}`}>
      <MonoLabel tone={highlight ? 'indigo' : undefined} className="block">
        {label}
      </MonoLabel>
      {addressLines(address).map((line) => (
        <p key={line} className="text-body text-foreground">{line}</p>
      ))}
    </div>
  );
}

export function AddressReviewDialog({
  open,
  entered,
  result,
  onUseSuggested,
  onKeepAsEntered,
  onEditAddress,
  renewal = false,
}: AddressReviewDialogProps) {
  const { t, i18n } = useTranslation('forms');
  /** #2752: the renewal line when this is a renewal and the language has it. */
  const renewalLine = (): boolean => {
    if (!renewal) return false;
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    if (lng === 'en' || lng.startsWith('en-')) return true;
    const value: unknown = i18n.getResource(lng, 'forms', 'address.unknownBodyRenewal');
    return typeof value === 'string' && value.trim() !== '';
  };
  const corrected = result.verdict === 'corrected' && result.normalized;
  /** #2793: each explanation line in the family's language. A line the
   *  server coded is translated; an unknown code, or an older server that
   *  sends no codes, falls back to the server's English message. */
  const componentName = (type: string): string =>
    isKnownComponent(type) ? t(`address.components.${type}`) : type.replace(/_/g, ' ');
  const explanationLines: string[] = (result.messages ?? []).map((message, i) => {
    const entry = result.codes?.[i];
    if (!entry || !isKnownCode(entry.code)) return message;
    const component = entry.params?.component;
    // The catalog placeholder is {{part}}: the guard on English catalog
    // values keeps engineer words such as "component" out of copy.
    return t(`address.codes.${entry.code}`, {
      part: component ? componentName(component) : '',
    });
  });

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onEditAddress(); }}>
      <DialogContent data-testid="address-review-dialog" className="sm:max-w-md flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="font-serif font-normal">
            {corrected ? t('address.correctedTitle') : t('address.unknownTitle')}
          </DialogTitle>
          <DialogDescription>
            {corrected ? t('address.correctedBody') : renewalLine() ? t('address.unknownBodyRenewal') : t('address.unknownBody')}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 -mx-6 px-6 space-y-3">
          {explanationLines.length > 0 && (
            <ul className="text-caption text-stevens-ink-3 space-y-1">
              {explanationLines.map((line, i) => (
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          )}
          {corrected && result.normalized && (
            <AddressCard label={t('address.suggested')} address={result.normalized} highlight />
          )}
          <AddressCard label={corrected ? t('address.asEntered') : t('address.yours')} address={entered} />
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2 sm:space-x-0">
          {corrected ? (
            <>
              <Button variant="brand" onClick={onUseSuggested}>{t('address.useSuggested')}</Button>
              <Button variant="outline" onClick={onKeepAsEntered}>{t('address.keepAsEntered')}</Button>
            </>
          ) : (
            <Button variant="brand" onClick={onKeepAsEntered}>{t('address.continueWithMine')}</Button>
          )}
          <Button variant="ghost" className="text-stevens-ink-3" onClick={onEditAddress}>
            {t('address.goBack')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
